import React, { useState, useRef } from "react";
import Icon from "../../../components/AppIcon";

const ProfilePhotoUpload = ({
  currentPhoto,
  onPhotoUpload,
  disabled = false,
  userName = "User",
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e?.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e?.dataTransfer?.files || []);
    const imageFile = files?.find((file) => file?.type?.startsWith("image/"));

    if (imageFile) {
      await handleFileUpload(imageFile);
    }
  };

  const handleFileInputChange = async (e) => {
    if (disabled) return;

    const file = e?.target?.files?.[0];
    if (file && file?.type?.startsWith("image/")) {
      await handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    // Validate file size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes?.includes(file?.type)) {
      alert("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    setIsUploading(true);
    try {
      await onPhotoUpload(file);
    } catch (error) {
      console.error("Error uploading photo:", error);
      alert("Error uploading photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef?.current?.click();
    }
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        ?.map((n) => n?.[0])
        ?.join("")
        ?.toUpperCase()
        ?.slice(0, 2) || "U"
    );
  };

  return (
    <div className="">
      <div
        className={` w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 transition-all duration-200 ${
          disabled
            ? "border-border cursor-default"
            : isDragOver
            ? "border-primary border-dashed cursor-pointer"
            : "border-border hover:border-primary cursor-pointer"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {isUploading ? (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : currentPhoto ? (
          <img
            src={currentPhoto}
            alt={`${userName}'s profile`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <span className="text-2xl md:text-3xl font-semibold text-muted-foreground">
              {getInitials(userName)}
            </span>
          </div>
        )}

        {/* Upload Overlay */}
        {!disabled && !isUploading && (
          <div className="absolute .inset-0a bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
            <div className="text-center">
              <Icon
                name="Camera"
                size={24}
                className="text-white mx-auto mb-1"
              />
              <span className="text-white text-xs font-medium">
                {currentPhoto ? "Change" : "Upload"}
              </span>
            </div>
          </div>
        )}

        {/* Drag and Drop Overlay */}
        {isDragOver && (
          <div className="absolute .inset-0a bg-primary bg-opacity-80 flex items-center justify-center">
            <div className="text-center">
              <Icon
                name="Upload"
                size={28}
                className="text-white mx-auto mb-2"
              />
              <span className="text-white text-sm font-medium">
                Drop photo here
              </span>
            </div>
          </div>
        )}
      </div>

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Mobile Camera Button */}
      {!disabled && (
        <button
          onClick={handleClick}
          className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors md:hidden"
        >
          <Icon name="Camera" size={16} />
        </button>
      )}

      {/* Help Text */}
      {!disabled && (
        <div className="mt-2 text-center">
          <p className="text-xs text-muted-foreground">
            Drag & drop or click to upload
          </p>
          <p className="text-xs text-muted-foreground">
            JPEG, PNG, WebP • Max 5MB
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUpload;
